import React from 'react';
import _ from 'lodash';
import { unformatAmount, getTimeRange, parseAmount } from 'app/utils/parsers';
import history from 'app/routes/history';
import Table from 'app/components/general/Table';
import Button from 'app/components/general/Button';
import PaymentBadge from 'app/components/general/PaymentBadge';

import { IPaymentCollection } from 'app/ducks/paymentCollections/types';
import moment from 'moment';

import PlusIcon from 'app/images/icons/plus.svg';

const filterOptions = [
  {
    label: 'Status',
    accessor: 'paymentCollectionStatus',
    type: 'select',
    options: [
      { id: 'pending', name: 'Unpaid' },
      { id: 'paid', name: 'Paid' },
    ],
  },
  {
    label: 'Team',
    accessor: 'payeeName',
    type: 'text',
  },
  {
    label: 'Reference',
    accessor: 'paymentReference',
    type: 'text',
  },
  {
    label: 'Amount',
    accessor: 'amount',
    type: 'currency',
  },
  {
    label: 'Section',
    accessor: 'groupSectionName',
    type: 'text',
  },
  {
    label: 'Date Paid',
    accessor: 'paidAt',
    type: 'datetime',
  },
  {
    label: 'Date Requested',
    accessor: 'createdAt',
    type: 'datetime',
  },
];

interface IPaymentCollectionsTableTeamsProps {
  actions: any;
  paymentCollectionsById: IPaymentCollection[];
  paymentCollectionIds: string[];
  headerTitle?: string;
  isLoading?: boolean;
  hideButtons?: boolean;
  manual?: boolean;
  initialFilterConfig?: any;
  persistentFilterConfig?: any;
  pagination?: any;
  selectPaymentCollection: (value) => void;
}

const constructPaymentCollectionsData = (paymentCollectionsById, paymentCollectionIds) => {
  if (
    paymentCollectionsById !== undefined
    && !_.isEmpty(paymentCollectionsById)
    && paymentCollectionIds !== undefined
  ) {
    const PaymentCollectionRows = _.reduce(
      paymentCollectionIds,
      (acc, id) => {
        if (!_.isEmpty(paymentCollectionsById[id])) {
          acc.push(paymentCollectionsById[id]);
        }
        return acc;
      },
      [],
    );

    return PaymentCollectionRows;
  }
  return [];
};

const onFetchData = async (props, state, instance, tableState) => {
  const { pageSize } = state;
  const pageNumber = state.page;
  const sort = instance.state.sorted;
  const filter = state.filtered;
  // console.log(props, filter);
  if (tableState.filtersApplied) {
    if (
      props.appReady
      && !props.isLoading
      && (pageNumber !== props.pagination.page.number
        || (!_.isEmpty(sort) && sort !== props.pagination.sort)
        || filter !== props.pagination.filter)
    ) {
      const amount = _.find(filter, (f) => f.id === 'amountInCents');
      if (amount) {
        const cents = (unformatAmount(amount.value) * 100).toString();
        _.remove(filter, (f: any) => f.id === 'amountInCents');
        filter.push({ id: 'amountInCents', value: cents });
      }
      const paidAt = _.find(filter, (f) => f.id === 'paidAt');
      if (paidAt) {
        const { sinceTimeParsed, untilTimeParsed } = getTimeRange(paidAt.value[0], paidAt.value[1]);
        _.remove(filter, (f: any) => f.id === 'paidAt');
        filter.push({ id: 'paidAt.since', value: sinceTimeParsed });
        filter.push({ id: 'paidAt.until', value: untilTimeParsed });
      }
      const createdAt = _.find(filter, (f) => f.id === 'createdAt');
      if (createdAt) {
        const { sinceTimeParsed, untilTimeParsed } = getTimeRange(createdAt.value[0], createdAt.value[1]);
        _.remove(filter, (f: any) => f.id === 'createdAt');
        filter.push({ id: 'createdAt.since', value: sinceTimeParsed });
        filter.push({ id: 'createdAt.until', value: untilTimeParsed });
      }
      await props.actions.getAllPaymentCollections({
        page: {
          size: pageSize,
          number: pageNumber,
        },
        sort,
        filter,
      });
    }
  }
};

const smallColumn = 'col col-2 col-lg-2';
const flexColumn = 'col col-2 d-none d-md-flex col-xl-1';
const mediumColumn = 'col col-6 col-md-3 col-lg-3 col-xl-2';

export default class PaymentCollectionsTableTeams extends React.Component<IPaymentCollectionsTableTeamsProps> {
  static defaultProps: IPaymentCollectionsTableTeamsProps = {
    manual: true,
    actions: undefined,
    paymentCollectionsById: undefined,
    paymentCollectionIds: undefined,
    initialFilterConfig: undefined,
    persistentFilterConfig: undefined,
    isLoading: false,
    pagination: undefined,
    selectPaymentCollection: undefined,
  }

  handleRequestPayment = (): void => {
    history.push('/payments/segments/requests/new');
  }

  handleClick = (paymentRequestId: string, id: string): void => {
    history.push(`/payments/segments/requests/detail/${paymentRequestId}/collection/${id}`);
  }

  render(): React.ReactNode {
    const data = constructPaymentCollectionsData(this.props.paymentCollectionsById, this.props.paymentCollectionIds);

    const columns = [
      {
        id: 'status',
        Header: 'Status',
        className: smallColumn,
        headerClassName: smallColumn,
        accessor: (row) => ({
          paymentCollectionStatusHumanised: row.paymentCollectionStatusHumanised,
          paymentCollectionStatusColour: row.paymentCollectionStatusColour,
        }),
        Cell: (cellProps) => (
          <PaymentBadge
            className="event-card-badge"
            paymentText={cellProps.value.paymentCollectionStatusHumanised}
          />
        ),
      },
      {
        Header: 'Reference',
        accessor: 'paymentReference',
        className: smallColumn,
        headerClassName: smallColumn,
      },
      {
        Header: 'Amount',
        accessor: 'amountInCents',
        className: smallColumn,
        headerClassName: smallColumn,
        Cell: (cellProps) => parseAmount(cellProps.value),
      },
      {
        Header: 'Team',
        accessor: 'payeeName',
        className: smallColumn,
        headerClassName: smallColumn,
      },
      {
        Header: 'Section',
        accessor: 'sectionName',
        className: mediumColumn,
        headerClassName: mediumColumn,
      },
      {
        Header: 'Date Paid',
        accessor: 'paidAt',
        className: mediumColumn,
        headerClassName: mediumColumn,
        Cell: (cellProps) => (_.isEmpty(cellProps.value) ? '' : moment(cellProps.value).format('D MMM YYYY')),
      },
    ];

    const counterText = this.props.pagination.meta.totalCount === 1 ? '1 payment collection' : `${this.props.pagination.meta.totalCount || 0} payment collections`;

    return (
      <Table
        headerTitle={this.props.headerTitle}
        objectTitle="payment requests"
        filterTitle="Payment Request Collections Data"
        isLoading={this.props.isLoading}
        data={data}
        handleClick={(id, row) => {
          this.handleClick(row.payment?.id, id);
        }}
        counterText={counterText}
        columns={columns}
        manual={this.props.manual}
        filterable
        aggressiveLoader
        onFetchData={(props, state, instance, tableState) => onFetchData(this.props, props, state, tableState)}
        filterOptions={filterOptions}
        filterConfig={this.props.initialFilterConfig}
        persistentFilterConfig={this.props.persistentFilterConfig}
        pagination={this.props.pagination}
      >
        {!this.props.hideButtons && (
          <Button btnIcon={PlusIcon} onClick={this.handleRequestPayment} className="btn-thinner btn-2">
            Add Person to Request
          </Button>
        )}
      </Table>
    );
  }
}
